import { gwiCommissionFor } from '../../../utils/gwiCommission';
import type { HosUtilizationDay } from '../../../services/hosReport';

export interface PayLine {
  id: number; pro: number; date: string; terminal: string; driver: string; driver_name: string;
  base: number | null; fuel: number; gwicommission: string;
}
export interface HosDay extends HosUtilizationDay {
  samsara_driver_id: string; driver_name: string | null; isMule?: boolean;
}
export interface MarginRow {
  pronumber: number; date: string; billto_id: number; customer_id: number;
  billto: string; customer: string; terminal: string; sales_rep: string; sales_rep_name?: string; dispatcher: string; dispatcher_name?: string;
  revenue: number; profit: number;
}
export interface TurnRow {
  date: string; terminal: string; import_pro: number; export_pro: number;
  reuse_location: string; container: string; export_customer: string;
  scac: string; size: string; equipment_type: string; reused_by: string;
}
export interface UnbilledRow {
  axis_value: string; sub_value: string; category: string; row_count: number;
  total_cost: number; total_revenue: number; total_margin: number;
}
export const percent = (value: number | null | undefined) => value == null ? '—' : `${value.toFixed(1)}%`;
export const money = (value: number | null | undefined) => value == null ? '—' : value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export function summarizePay(lines: PayLine[]) {
  const days = new Map<string, number>();
  let net = 0, base = 0, fuel = 0, commission = 0;
  const missing = lines.filter(row => row.base == null).length;
  for (const line of lines) {
    const values = gwiCommissionFor(line.base, line.gwicommission, line.fuel);
    net += values.net ?? 0; base += line.base ?? 0; fuel += line.fuel; commission += values.commission ?? 0;
    const key = JSON.stringify([line.driver, line.date]);
    days.set(key, (days.get(key) ?? 0) + (values.net ?? 0));
  }
  return { net, base, fuel, commission, missing, days: days.size,
    average: !missing && days.size ? net / days.size : null,
    goalDays: !missing ? [...days.values()].filter(amount => amount >= 1100).length : null };
}

export function payGroups(lines: PayLine[], daily: boolean) {
  const groups = new Map<string, PayLine[]>();
  for (const line of lines) {
    const key = JSON.stringify([line.terminal, line.driver, daily ? line.date : '']);
    groups.set(key, [...(groups.get(key) ?? []), line]);
  }
  return [...groups].map(([id, rows]) => ({ id, terminal: rows[0].terminal, driver: rows[0].driver,
    driver_name: rows[0].driver_name, date: daily ? rows[0].date : '', ...summarizePay(rows) }))
    .sort((a, b) => a.terminal.localeCompare(b.terminal) || a.driver_name.localeCompare(b.driver_name) || a.date.localeCompare(b.date));
}

export function marginGroups(rows: MarginRow[], primary: 'billto' | 'customer' | 'sales_rep' | 'terminal', breakdown?: 'terminal' | 'sales_rep' | 'dispatcher') {
  const groups = new Map<string, { id: string; name: string; breakdown: string; pros: number; revenue: number; profit: number; source: MarginRow[] }>();
  for (const row of rows) {
    const id = JSON.stringify([primary === 'terminal' ? row.terminal : primary === 'sales_rep' ? row.sales_rep : row[`${primary}_id`], (breakdown ? row[breakdown] : '')]);
    const group = groups.get(id) ?? { id, name: primary === 'terminal' ? (row.terminal || 'Unassigned') : primary === 'sales_rep' ? (row.sales_rep_name || row.sales_rep) : `${row[primary]} (${row[`${primary}_id`] ?? 'Unassigned'})`, breakdown: (breakdown ? row[breakdown] : ''), pros: 0, revenue: 0, profit: 0, source: [] };
    group.source.push(row); group.pros++; group.revenue += row.revenue; group.profit += row.profit; groups.set(id, group);
  }
  return [...groups.values()].map(row => ({ ...row, cost: row.revenue - row.profit, margin: row.revenue > 0 ? 100 * row.profit / row.revenue : null }))
    .sort((a, b) => a.name.localeCompare(b.name) || a.breakdown.localeCompare(b.breakdown));
}

export function hosShares(rows: Pick<HosUtilizationDay, 'driving_minutes' | 'on_duty_minutes'>[]) {
  const complete = rows.filter(row => row.driving_minutes != null && row.on_duty_minutes != null);
  const drive = complete.reduce((sum, row) => sum + row.driving_minutes!, 0);
  const work = complete.reduce((sum, row) => sum + row.on_duty_minutes!, 0);
  return { driving: work > 0 ? 100 * drive / work : null, other: work > 0 ? 100 * (work - drive) / work : null, days: complete.length };
}
